import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ActivityMonitor from "./Interview-Detectors/ActivityMonitor";
import OutsideClickDetector from "./Interview-Detectors/DisplayDetection";
import FaceDetector from "./Interview-Detectors/FaceDetector";
import FullScreenEnabler from "./Interview-Detectors/FullScreenEnabler";
import FullscreenDetector from "./Interview-Detectors/FullscreenDetector";
import BanInspecting from "./Interview-Detectors/InspectionDetector";
import TabSwitchDetector from "./Interview-Detectors/TabSwitchDetector";
import RestrictCopyPaste from "./Interview-Detectors/TextCopyRestrictor";
import Question from "./rightPart/Question";
import { getExamToStart } from "../../api/action/exam";
import { resolveEducationExamRouteContext } from "../../domains/education/routes/examRouteContext";
import { getExamConfiguration, isAcademyExam, resolveMonitoringRequired } from "../../domains/education/services/examMonitoring";

const ExamWorkspace = ({ monitoringRequired, initialExamData }) => {
  const examContent = (
    <div
      className="flex flex-col min-h-screen bg-cover bg-center bg-no-repeat pt-8"
      style={{
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      {monitoringRequired && (
        <>
          <FullScreenEnabler />
          <FullscreenDetector />
          <BanInspecting />
          <OutsideClickDetector />
          <ActivityMonitor />
          <FaceDetector />
          <TabSwitchDetector />
        </>
      )}
      <Question initialExamData={initialExamData} />
    </div>
  );

  return monitoringRequired ? <RestrictCopyPaste>{examContent}</RestrictCopyPaste> : examContent;
};

const ExamManagement = () => {
  const location = useLocation();
  const examContext = useMemo(() => resolveEducationExamRouteContext({
    search: location.search,
    state: location.state,
    storage: typeof window !== 'undefined' ? window.localStorage : null,
  }), [location.search, location.state]);
  const [loadState, setLoadState] = useState({ status: 'loading', monitoringRequired: true, examData: null });
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let active = true;
    if (!examContext.examId) {
      setLoadState({ status: 'error', monitoringRequired: true, examData: null });
      return () => { active = false; };
    }

    setLoadState({ status: 'loading', monitoringRequired: true, examData: null });
    getExamToStart(examContext.examId)
      .then((response) => {
        const examData = response?.data && !response.primaryId ? response.data : response;
        const configuration = getExamConfiguration(examData);
        if (!configuration?.primaryId) throw new Error('Exam configuration unavailable');
        if (!active) return;
        setLoadState({
          status: 'ready',
          monitoringRequired: isAcademyExam(configuration) ? resolveMonitoringRequired(configuration) : true,
          examData: Array.isArray(examData) ? examData[0] : examData,
        });
      })
      .catch(() => {
        if (active) setLoadState({ status: 'error', monitoringRequired: true, examData: null });
      });

    return () => { active = false; };
  }, [examContext.examId, retryToken]);

  if (loadState.status === 'loading') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-600">Preparing exam…</div>;
  }

  if (loadState.status === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
        <p className="text-sm text-red-700">We could not verify the exam monitoring configuration.</p>
        <button type="button" className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => setRetryToken((current) => current + 1)}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <ExamWorkspace
      monitoringRequired={loadState.monitoringRequired}
      initialExamData={loadState.examData}
    />
  );
};

export default ExamManagement;
